import React, {
    useMemo,
    useState,
    useEffect,
    useRef,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import AnimateHeight from '@mlb/react-patterns/components/AnimateHeight/animateHeight';
import { IconWrapper } from '@mlb/react-patterns/components/Icon/icon.style.tsx';
import COLORS from '@mlb/react-patterns/style/universal.color';
import Button from '@mlb/react-patterns/components/Button/button.tsx';

import { getChangeData, getParticipantPlacement } from './utils';
import Opponent from './Opponent';

const Opponent2 = styled(Opponent)`
    margin-top: 10px;
`;

const AddOpponentBtn = styled(Button)`
    padding: 0;
    font-weight: normal;

    span {
        margin-left: 10px;
    }

    ${IconWrapper} {
        vertical-align: unset;
    }
`;

const TOP = 'TOP';
const BOTTOM = 'BOTTOM';

const Opponents = (props) => {

    const {
        componentConfig,
        fields,
        onChange,
        defaultField,
        modifierFieldMap,
        modifiers,
        stateFieldMap,
        defaultParticipantState,
        defaultOptionValue,
        removeOpponentLabel,
        topHeader,
        bottomHeader,
        suggestionsKey,
        placeholder,
        typeaheadFieldId,
        opposingModifiers,
    } = props;

    const [
        participants,
        setParticipants,
    ] = useState({
        [TOP]: null,
        [BOTTOM]: null,
    });

    const {
        topParticipant = null,
        bottomParticipant = null,
        state,
    } = useMemo(() => {

        let participantA = null;
        let participantB = null;
        let participantAState = { ...defaultParticipantState };
        let participantBState = { ...defaultParticipantState };

        fields.forEach(field => {

            const { selected } = field;
            const fieldId = Number(field.id);

            for (let i = 0; i < selected.length; i++) {

                if (!participantA) {

                    participantA = selected[i];

                } else if (participantA.value !== selected[i].value && !participantB) {

                    participantB = selected[i];

                }

                const stateMap = stateFieldMap[fieldId];

                if (!stateMap) {

                    continue;

                }

                if (participantA && participantA.value === selected[i].value) {

                    participantAState[stateMap.stateProp] = stateMap.option;

                }

                if (participantB && participantB.value === selected[i].value) {

                    participantBState[stateMap.stateProp] = stateMap.option;

                }

            };

        });

        return getParticipantPlacement({
            participants,
            participantA,
            participantB,
            participantAState,
            participantBState,
            TOP,
            BOTTOM,
        });

    }, [
        fields,
        participants,
        defaultParticipantState,
    ]);

    useEffect(() => setParticipants({
        [TOP]: topParticipant,
        [BOTTOM]: bottomParticipant,
    }), [
        topParticipant,
        bottomParticipant,
    ]);

    const [
        showOpponent,
        setShowOpponent,
    ] = useState(!!bottomParticipant);

    const hasShownOpponent = useRef(!!bottomParticipant);

    useEffect(() => {

        if(hasShownOpponent.current) {

            return;

        }

        setShowOpponent(!!bottomParticipant);
        hasShownOpponent.current = !!bottomParticipant;

    }, [
        setShowOpponent,
        bottomParticipant,
        hasShownOpponent,
    ]);

    const onTypeaheadChangeHandler = (data, position) => {

        const {
            opponent,
            opponentPosition,
            opponentState,
        } = (position === TOP) ? {
            opponent: participants[BOTTOM],
            opponentPosition: BOTTOM,
            opponentState: state[BOTTOM],
        } : {
            opponent: participants[TOP],
            opponentPosition: TOP,
            opponentState: state[TOP],
        };

        const selected = [...data.selected];

        if (
            opponent
            && Object.values(opponentState).every(modifier => modifier.value === defaultOptionValue)
        ) {

            selected.push(opponent);

        }

        if (data.event === 'deselect-option') {

            setParticipants({
                [position]: null,
                [opponentPosition]: opponent,
            });

            for (const modifier in modifierFieldMap) {

                if (state[position][modifier].value === defaultOptionValue) {

                    continue;

                }

                let deselectField = modifierFieldMap[modifier][state[position][modifier].value];

                onChange({
                    event: data.event,
                    changes: data.changes,
                    selected,
                }, deselectField);

            }

        } else {

            setParticipants({
                [position]: data.changes,
                [opponentPosition]: opponent,
            });

        }

        if (
            opposingModifiers.length
            && participants[position] === null
            && !Object.keys(opponentState).filter(mod => opposingModifiers.includes(mod))
                .every(key => opponentState[key].value === defaultOptionValue)
        ) {

            // If opposingModifiers are passed in and previously set opponent has entered an "opposing modifier"
            // Then, when we are adding a second oponent we will not want to use the `defaultField` but the `oppositeField`
            // https://baseball.atlassian.net/browse/BDVP-1830

            opposingModifiers.forEach((uMod) => {

                if(opponentState[uMod].value === defaultOptionValue) {

                    return;

                }
                const opposite = modifiers[uMod].filter((mod) => mod.value !== defaultOptionValue && mod.value !== opponentState[uMod].value)[0];
                const oppositeField = modifierFieldMap[uMod][opposite.value];

                onChange({
                    event: data.event,
                    changes: data.changes,
                    selected,
                }, oppositeField);

            });

            return;

        }

        onChange({
            event: data.event,
            changes: data.changes,
            selected,
        }, defaultField);

    };

    const onChangeHandler = (data, input, position) => {

        const opponent = position === TOP ? BOTTOM : TOP;

        const { selected } = data;

        const {
            selectField,
            selectSelected,
            deselectField,
            deselectSelected,
        } = getChangeData({
            modifier: input,
            option: selected,
            defaultOptionValue,
            position,
            opponent,
            state,
            participants,
            modifierFieldMap,
        });

        if (selectField) {

            onChange({
                event: 'select',
                changes: participants[position],
                selected: selectSelected,
                preventUpdateSearchQuery: !!deselectField,
            }, selectField);

        }

        if (deselectField) {

            onChange({
                event: 'deselect',
                changes: participants[position],
                selected: deselectSelected,
            }, deselectField);

        }

    };

    return (
        <>
            <Opponent
                header={topHeader}
                config={componentConfig}
                selected={participants[TOP]}
                state={state[TOP]}
                modifiers={modifiers}
                onChange={(data, input) => onChangeHandler(data, input, TOP)}
                onTypeaheadChange={(data) => onTypeaheadChangeHandler(data, TOP)}
                disabled={!!!participants[TOP]}
                suggestionsKey={suggestionsKey}
                placeholder={placeholder}
                typeaheadFieldId={typeaheadFieldId}
                excludeId={participants[BOTTOM]?.value}
            />
            <AnimateHeight
                expanded={showOpponent}
                transitionDuration={0.2}
                collapsible={
                    <Opponent2
                        header={bottomHeader}
                        config={componentConfig}
                        selected={participants[BOTTOM]}
                        state={state[BOTTOM]}
                        modifiers={modifiers}
                        onChange={(data, input) => onChangeHandler(data, input, BOTTOM)}
                        onTypeaheadChange={(data) => onTypeaheadChangeHandler(data, BOTTOM)}
                        disabled={!!!participants[BOTTOM]}
                        disabledHeader={!!!participants[BOTTOM] && !!!participants[TOP]}
                        disabledTypeahead={!!!participants[TOP]}
                        suggestionsKey={suggestionsKey}
                        placeholder={placeholder}
                        typeaheadFieldId={typeaheadFieldId}
                        excludeId={participants[TOP]?.value}
                    />
                }
            />
            <AddOpponentBtn
                label={showOpponent ? removeOpponentLabel : 'Add Opponent'}
                iconName={showOpponent ? 'accordion-contract' : 'accordion-expand'}
                iconColor={COLORS.linkBlueOnDark}
                iconSize={13}
                size={'md'}
                styleName={'default'}
                shape={'hollow-borderless'}
                hasHover={false}
                handlers={{
                    onClick: () => {

                        if (!!participants[BOTTOM] && showOpponent) {

                            onTypeaheadChangeHandler({
                                event: 'deselect-option',
                                changes: participants[BOTTOM],
                                selected: [],
                            }, BOTTOM);

                        }
                        setShowOpponent(!showOpponent);

                        if (!hasShownOpponent.current) {

                            hasShownOpponent.current = true;

                        }

                    },
                }}
            />
        </>
    );

};

Opponents.propTypes = {
    componentConfig: PropTypes.object.isRequired,
    fields: PropTypes.arrayOf(
        PropTypes.shape({
            selected: PropTypes.array.isRequired,
        }).isRequired
    ).isRequired,
    onChange: PropTypes.func.isRequired,
    defaultField: PropTypes.object.isRequired,
    modifierFieldMap: PropTypes.object.isRequired,
    modifiers: PropTypes.object.isRequired,
    stateFieldMap: PropTypes.object.isRequired,
    defaultParticipantState: PropTypes.object.isRequired,
    defaultOptionValue: PropTypes.string.isRequired,
    removeOpponentLabel: PropTypes.string.isRequired,
    topHeader: PropTypes.string.isRequired,
    bottomHeader: PropTypes.string.isRequired,
    suggestionsKey: PropTypes.string.isRequired,
    placeholder: PropTypes.string.isRequired,
    typeaheadFieldId: PropTypes.number.isRequired,
    opposingModifiers: PropTypes.array,
};

Opponents.defaultProps = {
    opposingModifiers: [],
};

export default Opponents;
